/*!
 * Dé Pakketbrievenbus – Keuzehulp v1.0
 * Web components: <keuzehulp-quiz> en <keuzehulp-banner>
 * Geen dependencies. Alle vragen en productdata komen uit snippets/keuzehulp-config.liquid.
 */
(() => {
  'use strict';
  if (customElements.get('keuzehulp-quiz')) return;

  /* ------------------------------------------------------------------ helpers */
  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const sortDesc = (a) => [...a].sort((x, y) => y - x);
  const fits = (parcel, opening) => {
    const p = sortDesc(parcel);
    const o = sortDesc(opening);
    return p.every((v, i) => v <= o[i]);
  };
  const volume = (a) => a.reduce((x, y) => x * y, 1);
  /* Maatklasse op basis van inhoud van het pakketvak (cm³):
     0 = compact (Harolds, MPB Medium), 1 = ruim (Prestige, MPB Large), 2 = XXL */
  const tierOf = (vol) => (vol < 15000 ? 0 : vol < 40000 ? 1 : 2);
  const fmtDims = (a) => a.map((n) => String(n).replace('.', ',')).join(' × ') + ' cm';
  const uuid = () =>
    window.crypto && crypto.randomUUID
      ? crypto.randomUUID()
      : Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  const now = () => (window.performance ? performance.now() : Date.now());
  const img = (src, width) => {
    if (!src) return '';
    const url = src.startsWith('//') ? 'https:' + src : src;
    return width ? url.replace(/([?&])width=\d+/, `$1width=${width}`) : url;
  };

  /* Neutrale lijntekeningen voor de stijlvraag: laten de vorm zien, niet een merk of product. */
  const svg = (inner) =>
    `<svg viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;
  const slot = '<path d="M26 36h12"/><path d="M29 47h6"/>';
  const ground = '<path d="M10 58h44" opacity=".35"/>';
  const ICONS = {
    puntdak: svg(`<path d="M11 28 32 10l21 18"/><path d="M16 24v34h32V24"/>${slot}${ground}`),
    tuindak: svg(`<path d="M11 25 53 15"/><path d="M16 24v34h32V18"/>${slot}${ground}`),
    strak: svg(`<path d="M12 20h40"/><path d="M16 20v38h32V20"/>${slot}${ground}`),
    bol: svg(`<path d="M16 30a16 16 0 0 1 32 0"/><path d="M16 30v28h32V30"/>${slot}${ground}`),
    geen: svg(`<rect x="16" y="20" width="32" height="38" rx="3" stroke-dasharray="4 4"/><path d="M28 34a4 4 0 1 1 5.5 3.7c-1 .4-1.5 1.2-1.5 2.3v1"/><path d="M32 46h.01"/>${ground}`),
  };

  /* Alle parameters die de keuzehulp gebruikt. Bij iedere push worden ze eerst
     op null gezet: GTM "onthoudt" anders waarden uit eerdere pushes. */
  const BASE_KEYS = [
    'kh_quiz_id', 'kh_session_id', 'kh_placement', 'kh_source_product', 'kh_source_collection',
    'kh_step_number', 'kh_step_name', 'kh_total_steps', 'kh_answer', 'kh_answer_label',
    'kh_time_on_step', 'kh_answers', 'kh_duration', 'kh_reason', 'kh_position',
    'kh_result_primary', 'kh_result_primary_id', 'kh_result_alt', 'kh_result_alt_id', 'kh_result_flags',
  ];

  /* ------------------------------------------------------------------ quiz */
  class KeuzehulpQuiz extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      try {
        this.config = JSON.parse(this.querySelector('[data-keuzehulp-config]').textContent);
      } catch (err) {
        console.error('[Keuzehulp] Configuratie kon niet worden gelezen', err);
        return;
      }

      const d = this.dataset;
      this.opts = {
        placement: d.placement || 'collectie',
        trackingMode: d.trackingMode || 'auto',
        itemIdFormat: d.itemIdFormat || 'merchant_center',
        afterAdd: d.afterAdd || 'cart',
        country: d.country || 'NL',
        currency: d.currency || 'EUR',
        sourceProduct: d.sourceProduct || null,
        sourceCollection: d.sourceCollection || null,
        cartAddUrl: d.cartAddUrl || '/cart/add.js',
        cartUpdateUrl: d.cartUpdateUrl || '/cart/update.js',
        cartUrl: d.cartUrl || '/cart',
        rootUrl: d.rootUrl || '/',
      };

      this.steps = this.config.steps || [];
      this.answerKeys = this.steps.map((s) => `kh_a_${s.id}`);
      this.root = this.querySelector('[data-kh-root]');
      this.money = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: this.opts.currency });
      this.candidates = this.buildCandidates();
      this.state = this.freshState();

      this.root.addEventListener('click', (e) => this.onClick(e));
      this._onPageHide = () => this.abandon('pagina_verlaten');
      window.addEventListener('pagehide', this._onPageHide);
      this.observeImpression();

      if (d.autostart !== 'true') return;
      /* In een pop-up start de sessie pas bij openen; zie <keuzehulp-banner>. */
    }

    disconnectedCallback() {
      window.removeEventListener('pagehide', this._onPageHide);
      if (this._io) this._io.disconnect();
    }

    freshState() {
      return {
        sessionId: uuid(),
        index: -1,
        answers: {},
        started: false,
        completed: false,
        abandoned: false,
        startedAt: 0,
        stepShownAt: 0,
        result: null,
      };
    }

    /* ---------- data */
    buildCandidates() {
      const list = [];
      Object.entries(this.config.products || {}).forEach(([handle, spec]) => {
        const live = spec.live;
        if (!live || !Array.isArray(live.variants)) return;
        live.variants.forEach((variant) => {
          const size = (spec.sizes || {})[variant.option1] || (spec.sizes || {})['*'];
          if (!size || !Array.isArray(size.parcel)) return;
          list.push({
            handle,
            spec,
            live,
            variant,
            size,
            volume: volume(size.parcel),
            label: size.label ? `${live.title} – ${size.label}` : live.title,
            shortLabel: `${handle}${size.label ? '_' + size.label.toLowerCase() : ''}`,
          });
        });
      });
      return list;
    }

    imageFor(handle) {
      const spec = (this.config.products || {})[handle];
      return spec && spec.live ? spec.live.image : null;
    }

    option(stepId) {
      const step = this.steps.find((s) => s.id === stepId);
      const value = this.state.answers[stepId];
      return step && value ? (step.options || []).find((o) => o.value === value) || null : null;
    }

    /* ---------- advieslogica */
    recommend() {
      const flags = [];
      const parcelOpt = this.option('pakketgrootte');
      const freqOpt = this.option('frequentie');
      const placeOpt = this.option('plaatsing');
      const styleOpt = this.option('stijl');
      const budgetOpt = this.option('budget');

      const parcel = parcelOpt && parcelOpt.parcel;
      const capacity = freqOpt && typeof freqOpt.capacity === 'number' ? freqOpt.capacity : 0;
      const max = budgetOpt && typeof budgetOpt.max === 'number' ? budgetOpt.max : null;
      const min = budgetOpt && typeof budgetOpt.min === 'number' ? budgetOpt.min : null;

      let pool = this.candidates.filter((c) => c.variant.available);
      if (!pool.length) return { primary: null, alternative: null, flags: ['niets_op_voorraad'] };

      // 1. Harde eis: het pakket moet passen.
      if (parcel) {
        const fitting = pool.filter((c) => fits(parcel, c.size.parcel));
        if (fitting.length) {
          pool = fitting;
        } else {
          const maxVol = Math.max(...pool.map((c) => c.volume));
          pool = pool.filter((c) => c.volume === maxVol);
          flags.push('geen_pasvorm');
        }
      }

      // 2. Harde eis: plaatsing (wordt versoepeld als niets overblijft).
      if (placeOpt && (placeOpt.mount || placeOpt.rear)) {
        const placed = pool.filter(
          (c) => (!placeOpt.mount || (c.spec.mount || []).includes(placeOpt.mount)) && (!placeOpt.rear || c.spec.rear)
        );
        if (placed.length) pool = placed;
        else flags.push('plaatsing_versoepeld');
      }

      // 3. Capaciteit: maatklasse van het pakket, per stap frequentie één klasse ruimer.
      const tiers = pool.map((c) => tierOf(c.volume));
      const minTier = Math.min(...tiers);
      const maxTier = Math.max(...tiers);
      const needTier = parcel ? tierOf(volume(parcel)) : minTier;
      const targetTier = Math.max(minTier, Math.min(needTier + capacity, maxTier));
      const minVol = Math.min(...pool.map((c) => c.volume));

      // 4. Scoren op capaciteit, stijl, budget en prijs.
      const scored = pool.map((c) => {
        let score = -15 * Math.abs(tierOf(c.volume) - targetTier);
        if (styleOpt && styleOpt.style && c.spec.style === styleOpt.style) score += 30;
        if (max !== null) {
          score += c.variant.price <= max ? 20 : -20 - (c.variant.price - max) / 1000;
        }
        // "Boven X": zachte voorkeur voor het ruimere segment, nooit een harde uitsluiting.
        if (min !== null) score += c.variant.price >= min ? 12 : -12;
        score -= c.variant.price / 10000; // lichte voorkeur voor voordeliger bij gelijke score
        score += (c.spec.priority || 0) / 10;
        return { ...c, score };
      });
      scored.sort((a, b) => b.score - a.score);

      const primary = scored[0] || null;
      const alternative = primary ? scored.find((c) => c.spec.family !== primary.spec.family) || null : null;

      if (max !== null && primary && primary.variant.price > max) flags.push('boven_budget');

      const ctx = { parcelOpt, capacity, placeOpt, styleOpt, max, min, minVol };
      if (primary) primary.reasons = this.reasons(primary, ctx);
      if (alternative) alternative.reasons = this.reasons(alternative, ctx);
      return { primary, alternative, flags };
    }

    reasons(c, { parcelOpt, capacity, placeOpt, styleOpt, max, min, minVol }) {
      const r = [];
      r.push(`Past pakketten tot ${fmtDims(c.size.parcel)}`);
      if (capacity > 0 && c.volume > minVol) r.push('Extra ruimte voor meerdere bezorgingen');
      if (placeOpt && placeOpt.rear && c.spec.rear) r.push('Aan de achterkant te legen, ideaal in een erfafscheiding');
      else if (placeOpt && placeOpt.mount === 'muur' && (c.spec.mount || []).includes('muur')) r.push('Geschikt voor montage aan de muur');
      else if (placeOpt && placeOpt.mount === 'vrijstaand' && (c.spec.mount || []).includes('vrijstaand'))
        r.push(c.spec.note === 'Inclusief paal' ? 'Vrijstaand te plaatsen, paal inbegrepen' : 'Vrijstaand te plaatsen');
      if (styleOpt && styleOpt.style && c.spec.style === styleOpt.style) r.push(`${styleOpt.label} uiterlijk, zoals je koos`);
      if ((max !== null && c.variant.price <= max) || (min !== null && c.variant.price >= min)) r.push('Past bij je budget');
      if (c.spec.warranty) r.push(`${c.spec.warranty} jaar garantie`);
      return r.slice(0, 4);
    }

    /* ---------- flow */
    start(reason) {
      if (this.state.started && !this.state.completed && !this.state.abandoned) {
        this.renderStep();
        return;
      }
      this.state = this.freshState();
      this.state.started = true;
      this.state.startedAt = now();
      this.track('keuzehulp_start', { kh_reason: reason || null });
      this.showStep(0);
    }

    showStep(i) {
      this.state.index = i;
      this.state.stepShownAt = now();
      const step = this.steps[i];
      this.track('keuzehulp_step_view', {
        kh_step_number: i + 1,
        kh_step_name: step.id,
      });
      this.renderStep();
    }

    answer(value) {
      const i = this.state.index;
      const step = this.steps[i];
      const opt = (step.options || []).find((o) => o.value === value);
      const label = opt ? opt.label : 'Overgeslagen';
      this.state.answers[step.id] = value;
      this.track('keuzehulp_answer', {
        kh_step_number: i + 1,
        kh_step_name: step.id,
        kh_answer: value,
        kh_answer_label: label,
        kh_time_on_step: Math.round((now() - this.state.stepShownAt) / 100) / 10,
      });
      if (i + 1 < this.steps.length) this.showStep(i + 1);
      else this.complete();
    }

    back() {
      const from = this.state.completed ? this.steps.length + 1 : this.state.index + 1;
      this.track('keuzehulp_back', { kh_step_number: from, kh_step_name: this.state.completed ? 'resultaat' : this.steps[this.state.index].id });
      if (this.state.completed) {
        this.state.completed = false;
        this.state.result = null;
        this.showStep(this.steps.length - 1);
      } else if (this.state.index > 0) {
        this.showStep(this.state.index - 1);
      }
    }

    complete() {
      this.state.completed = true;
      const result = this.recommend();
      this.state.result = result;
      const perAnswer = {};
      this.steps.forEach((s) => (perAnswer[`kh_a_${s.id}`] = this.state.answers[s.id] || null));

      this.track('keuzehulp_complete', {
        ...perAnswer,
        kh_step_number: this.steps.length + 1,
        kh_step_name: 'resultaat',
        kh_duration: Math.round((now() - this.state.startedAt) / 1000),
        kh_result_primary: result.primary ? result.primary.shortLabel : null,
        kh_result_primary_id: result.primary ? String(result.primary.variant.id) : null,
        kh_result_alt: result.alternative ? result.alternative.shortLabel : null,
        kh_result_alt_id: result.alternative ? String(result.alternative.variant.id) : null,
        kh_result_flags: result.flags.join(',') || null,
      });

      const shown = [result.primary, result.alternative].filter(Boolean);
      if (shown.length) this.trackEcommerce('view_item_list', shown.map((c, i) => this.gaItem(c, i)));
      this.renderResult();
    }

    restart() {
      this.track('keuzehulp_restart', {});
      this.state = this.freshState();
      this.start('opnieuw');
    }

    /* Sessie gestart maar niet afgerond en de bezoeker haakt af. */
    abandon(reason) {
      const s = this.state;
      if (!s || !s.started || s.completed || s.abandoned) return;
      s.abandoned = true;
      const step = this.steps[s.index];
      this.track('keuzehulp_abandon', {
        kh_reason: reason,
        kh_step_number: s.index + 1,
        kh_step_name: step ? step.id : null,
        kh_duration: Math.round((now() - s.startedAt) / 1000),
      });
    }

    /* ---------- winkelwagen */
    async addToCart(position, button) {
      const c = position === 0 ? this.state.result.primary : this.state.result.alternative;
      if (!c) return;
      const errorEl = this.root.querySelector('[data-kh-error]');
      if (errorEl) errorEl.textContent = '';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      try {
        const res = await fetch(this.opts.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: c.variant.id, quantity: 1, properties: { _keuzehulp: position === 0 ? 'advies' : 'alternatief' } }],
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Toevoegen mislukt');
        }

        // Antwoorden meegeven aan de bestelling (zichtbaar in Shopify-admin en in de checkout-pixel).
        await fetch(this.opts.cartUpdateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Keuzehulp gebruikt': 'ja',
              'Keuzehulp antwoorden': this.answersString(),
              'Keuzehulp advies': c.label,
              'Keuzehulp sessie': this.state.sessionId,
            },
          }),
        }).catch(() => {});

        await this.trackEcommerce('keuzehulp_add_to_cart', [this.gaItem(c, position)], {
          kh_position: position === 0 ? 'advies' : 'alternatief',
          value: c.variant.price / 100,
        }, true);

        document.dispatchEvent(new CustomEvent('keuzehulp:added', { detail: { variantId: c.variant.id, position } }));

        if (this.opts.afterAdd === 'checkout') window.location.href = `${this.opts.rootUrl.replace(/\/$/, '')}/checkout`;
        else if (this.opts.afterAdd === 'cart') window.location.href = this.opts.cartUrl;
        else {
          button.textContent = 'Toegevoegd ✓';
          button.removeAttribute('aria-busy');
        }
      } catch (err) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (errorEl) errorEl.textContent = `Dat lukte niet: ${err.message}. Probeer het opnieuw of neem contact met ons op.`;
      }
    }

    answersString() {
      return this.steps.map((s) => `${s.id}=${this.state.answers[s.id] || '-'}`).join('; ');
    }

    /* ---------- tracking */
    baseParams() {
      const p = {};
      BASE_KEYS.concat(this.answerKeys).forEach((k) => (p[k] = null));
      p.kh_quiz_id = this.config.quiz_id || 'keuzehulp';
      p.kh_session_id = this.state ? this.state.sessionId : null;
      p.kh_placement = this.opts.placement;
      p.kh_source_product = this.opts.sourceProduct;
      p.kh_source_collection = this.opts.sourceCollection;
      p.kh_total_steps = this.steps.length;
      p.kh_answers = this.state
        ? this.steps.filter((s) => this.state.answers[s.id]).map((s) => `${s.id}:${this.state.answers[s.id]}`).join('|') || null
        : null;
      return p;
    }

    gtmInTheme() {
      if (window.google_tag_manager) return true;
      return (window.dataLayer || []).some((e) => e && typeof e === 'object' && 'gtm.start' in e);
    }

    channels() {
      const mode = this.opts.trackingMode;
      const canPublish = !!(window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function');
      if (mode === 'datalayer') return { dl: true, pub: false };
      if (mode === 'shopify') return { dl: false, pub: canPublish };
      if (mode === 'beide') return { dl: true, pub: canPublish };
      // auto: GTM in het thema → dataLayer, anders → Shopify customer events (custom pixel)
      const theme = this.gtmInTheme();
      return { dl: theme || !canPublish, pub: !theme && canPublish };
    }

    /* Push naar dataLayer en/of Shopify customer events. Met waitForGtm wacht de
       functie (max. 1 s) tot GTM de tags heeft verstuurd, handig vóór een redirect. */
    emit(obj, waitForGtm) {
      const { dl, pub } = this.channels();
      let done = Promise.resolve();

      if (dl) {
        window.dataLayer = window.dataLayer || [];
        if (obj.ecommerce) window.dataLayer.push({ ecommerce: null });
        if (waitForGtm && this.gtmInTheme()) {
          done = new Promise((resolve) => {
            const t = setTimeout(resolve, 1000);
            window.dataLayer.push({ ...obj, eventCallback: () => { clearTimeout(t); resolve(); }, eventTimeout: 1000 });
          });
        } else {
          window.dataLayer.push(obj);
        }
      }
      if (pub) {
        const name = obj.event.startsWith('keuzehulp_') ? obj.event : `keuzehulp_${obj.event}`;
        try {
          window.Shopify.analytics.publish(name, JSON.parse(JSON.stringify(obj)));
        } catch (e) { /* publiceren mag de keuzehulp nooit breken */ }
        if (waitForGtm) done = Promise.all([done, new Promise((r) => setTimeout(r, 350))]);
      }
      this.dispatchEvent(new CustomEvent('keuzehulp:track', { bubbles: true, detail: obj }));
      return done;
    }

    track(event, params) {
      return this.emit({ event, ...this.baseParams(), ...params });
    }

    trackEcommerce(event, items, extra = {}, waitForGtm = false) {
      const { value, ...rest } = extra;
      return this.emit(
        {
          event,
          ...this.baseParams(),
          ...rest,
          ecommerce: {
            currency: this.opts.currency,
            ...(typeof value === 'number' ? { value } : {}),
            item_list_id: 'keuzehulp',
            item_list_name: 'Keuzehulp',
            items,
          },
        },
        waitForGtm
      );
    }

    gaItem(c, index) {
      const v = c.variant;
      let id;
      if (this.opts.itemIdFormat === 'sku') id = v.sku || String(v.id);
      else if (this.opts.itemIdFormat === 'variant_id') id = String(v.id);
      else id = `shopify_${this.opts.country}_${c.live.id}_${v.id}`;
      return {
        item_id: id,
        item_name: c.live.title,
        item_brand: c.live.vendor || undefined,
        item_category: c.live.type || undefined,
        item_variant: c.size.label || undefined,
        item_list_id: 'keuzehulp',
        item_list_name: 'Keuzehulp',
        index,
        price: v.price / 100,
        quantity: 1,
      };
    }

    observeImpression() {
      if (!('IntersectionObserver' in window)) return;
      this._io = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this._io.disconnect();
        // Bij de pop-up telt de banner als impressie; die meet <keuzehulp-banner> zelf.
        if (this.dataset.autostart !== 'true') this.track('keuzehulp_impression', {});
      }, { threshold: 0.5 });
      this._io.observe(this);
    }

    /* ---------- events */
    onClick(e) {
      const t = e.target.closest('button, a');
      if (!t || !this.root.contains(t)) return;
      if (t.matches('[data-kh-start]')) this.start('knop');
      else if (t.matches('[data-kh-option]')) this.answer(t.dataset.value);
      else if (t.matches('[data-kh-skip]')) this.answer('overgeslagen');
      else if (t.matches('[data-kh-back]')) this.back();
      else if (t.matches('[data-kh-restart]')) this.restart();
      else if (t.matches('[data-kh-add]')) this.addToCart(Number(t.dataset.position), t);
      else if (t.matches('[data-kh-view]')) {
        const pos = Number(t.dataset.position);
        const c = pos === 0 ? this.state.result.primary : this.state.result.alternative;
        if (c) this.trackEcommerce('select_item', [this.gaItem(c, pos)], { kh_position: pos === 0 ? 'advies' : 'alternatief' });
      } else if (t.matches('[data-kh-contact]')) this.track('keuzehulp_contact_click', {});
    }

    focusHeading() {
      const h = this.root.querySelector('[data-kh-focus]');
      if (!h) return;
      h.focus({ preventScroll: true });
      const r = this.getBoundingClientRect();
      if (r.top < 0 || r.top > window.innerHeight * 0.6) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ---------- rendering */
    renderStep() {
      const i = this.state.index;
      const step = this.steps[i];
      const total = this.steps.length;
      const selected = this.state.answers[step.id];
      const pct = Math.round((i / total) * 100);
      const hasImages = (step.options || []).some((o) => (o.icon && ICONS[o.icon]) || (o.image_from && this.imageFor(o.image_from)));

      const options = (step.options || [])
        .map((o) => {
          const src = o.image_from ? this.imageFor(o.image_from) : null;
          const icon = o.icon && ICONS[o.icon];
          const media = icon
            ? `<span class="kh__opt-media kh__opt-media--icon">${icon}</span>`
            : `<span class="kh__opt-media">${src ? `<img src="${esc(img(src, 240))}" alt="" loading="lazy" width="120" height="120">` : ''}</span>`;
          return `
          <button type="button" class="kh__opt${o.value === selected ? ' is-selected' : ''}" data-kh-option data-value="${esc(o.value)}" aria-pressed="${o.value === selected}">
            ${hasImages ? media : ''}
            <span class="kh__opt-text">
              <span class="kh__opt-label">${esc(o.label)}</span>
              ${o.description ? `<span class="kh__opt-desc">${esc(o.description)}</span>` : ''}
              ${o.hint ? `<span class="kh__opt-hint">${esc(o.hint)}</span>` : ''}
            </span>
          </button>`;
        })
        .join('');

      this.root.innerHTML = `
        <div class="kh__step" data-step="${esc(step.id)}">
          <div class="kh__progress">
            <div class="kh__bar" role="progressbar" aria-label="Voortgang" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}"><span style="width:${pct}%"></span></div>
            <span class="kh__count">Vraag ${i + 1} van ${total}</span>
          </div>
          <h3 class="kh__q" tabindex="-1" data-kh-focus>${esc(step.question)}</h3>
          ${step.help ? `<p class="kh__help">${esc(step.help)}</p>` : ''}
          <div class="kh__options${hasImages ? ' kh__options--media' : ''}">${options}</div>
          <div class="kh__nav">
            ${i > 0 ? '<button type="button" class="kh__link" data-kh-back>← Vorige vraag</button>' : '<span></span>'}
            ${step.optional ? '<button type="button" class="kh__link" data-kh-skip>Sla over →</button>' : ''}
          </div>
        </div>`;
      this.focusHeading();
    }

    card(c, position) {
      const v = c.variant;
      const src = v.image || c.live.image;
      const url = `${c.live.url}${c.live.variants.length > 1 ? `?variant=${v.id}` : ''}`;
      const compare = v.compare_at_price ? `<s class="kh__was">${this.money.format(v.compare_at_price / 100)}</s>` : '';
      return `
        <article class="kh__card${position === 0 ? ' kh__card--primary' : ''}">
          <span class="kh__badge">${position === 0 ? 'Ons advies' : 'Alternatief'}</span>
          <a class="kh__card-media" href="${esc(url)}" data-kh-view data-position="${position}" tabindex="-1" aria-hidden="true">
            ${src ? `<img src="${esc(img(src, 520))}" alt="" loading="lazy" width="260" height="260">` : ''}
          </a>
          <div class="kh__card-body">
            <h4 class="kh__card-title"><a href="${esc(url)}" data-kh-view data-position="${position}">${esc(c.label)}</a></h4>
            <p class="kh__price">${compare}<span>${this.money.format(v.price / 100)}</span></p>
            <ul class="kh__reasons">${(c.reasons || []).map((r) => `<li>${esc(r)}</li>`).join('')}</ul>
            ${c.spec.note ? `<p class="kh__note">${esc(c.spec.note)}</p>` : ''}
            <div class="kh__actions">
              <button type="button" class="kh__btn ${position === 0 ? 'kh__btn--primary' : 'kh__btn--secondary'}" data-kh-add data-position="${position}">In winkelwagen</button>
              <a class="kh__link" href="${esc(url)}" data-kh-view data-position="${position}">Bekijk product</a>
            </div>
          </div>
        </article>`;
    }

    renderResult() {
      const { primary, alternative, flags } = this.state.result;
      const contact = this.config.contact_url || '/pages/contact';
      const notices = [];
      if (flags.includes('geen_pasvorm')) notices.push('Geen van onze modellen is groot genoeg voor dit formaat. Dit is onze ruimste pakketbrievenbus.');
      if (flags.includes('plaatsing_versoepeld')) notices.push('Er is geen model dat precies bij jouw plaatsing past. Neem gerust contact op, dan denken we mee.');
      if (flags.includes('boven_budget')) notices.push('Dit advies ligt iets boven je budget, maar past het beste bij je wensen.');

      const summary = this.steps
        .filter((s) => this.state.answers[s.id] && this.state.answers[s.id] !== 'overgeslagen')
        .map((s) => {
          const o = (s.options || []).find((x) => x.value === this.state.answers[s.id]);
          return o ? `<li>${esc(o.label)}</li>` : '';
        })
        .join('');

      this.root.innerHTML = `
        <div class="kh__result">
          <div class="kh__progress">
            <div class="kh__bar" role="progressbar" aria-label="Voortgang" aria-valuemin="0" aria-valuemax="100" aria-valuenow="100"><span style="width:100%"></span></div>
            <span class="kh__count">Klaar</span>
          </div>
          <h3 class="kh__q" tabindex="-1" data-kh-focus>${primary ? 'Dit past het beste bij jou' : 'We helpen je graag persoonlijk verder'}</h3>
          ${summary ? `<ul class="kh__summary" aria-label="Jouw antwoorden">${summary}</ul>` : ''}
          ${notices.map((n) => `<p class="kh__notice">${esc(n)}</p>`).join('')}
          ${primary
            ? `<div class="kh__cards">${this.card(primary, 0)}${alternative ? this.card(alternative, 1) : ''}</div>`
            : '<p class="kh__help">Op dit moment is er geen passend model op voorraad.</p>'}
          <p class="kh__error" data-kh-error role="alert"></p>
          <div class="kh__nav">
            <button type="button" class="kh__link" data-kh-back>← Antwoord aanpassen</button>
            <button type="button" class="kh__link" data-kh-restart>Opnieuw beginnen</button>
            <a class="kh__link" href="${esc(contact)}" data-kh-contact>Twijfel je nog? Vraag advies</a>
          </div>
        </div>`;
      this.focusHeading();
    }
  }

  /* ------------------------------------------------------------------ banner + pop-up (productpagina) */
  class KeuzehulpBanner extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.dialog = this.querySelector('dialog');
      this.quiz = this.querySelector('keuzehulp-quiz');
      if (!this.dialog || !this.quiz) return;

      this.querySelectorAll('[data-kh-open]').forEach((b) => b.addEventListener('click', () => this.open()));
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog || e.target.closest('[data-kh-close]')) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('kh-lock');
        if (this.quiz.abandon) this.quiz.abandon('popup_gesloten');
        if (this._opener) this._opener.focus();
      });

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          customElements.whenDefined('keuzehulp-quiz').then(() => this.quiz.track && this.quiz.track('keuzehulp_impression', {}));
        }, { threshold: 0.5 });
        io.observe(this.querySelector('.khb__bar') || this);
      }
    }

    open() {
      this._opener = document.activeElement;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.documentElement.classList.add('kh-lock');
      this.quiz.start('popup');
    }
  }

  customElements.define('keuzehulp-quiz', KeuzehulpQuiz);
  if (!customElements.get('keuzehulp-banner')) customElements.define('keuzehulp-banner', KeuzehulpBanner);
})();
